// Тонкая обёртка над Telegram WebApp (грузится через CDN telegram-web-app.js в
// <head>). Разметку и поведение переносим 1:1 из gb, поэтому работаем с нативным
// window.Telegram.WebApp, а не с SDK-обёрткой.
import { t } from './i18n';

/** Палитра клиента Telegram. Все поля необязательны: старые клиенты шлют не всё. */
export interface TgThemeParams {
  bg_color?: string;
  secondary_bg_color?: string;
  section_bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  accent_text_color?: string;
  destructive_text_color?: string;
}

interface TgWebApp {
  initData: string;
  initDataUnsafe: { user?: { id: number; language_code?: string; username?: string } };
  colorScheme: 'light' | 'dark';
  themeParams: TgThemeParams;
  platform?: string; // 'android' | 'ios' | 'tdesktop' | 'macos' | 'web' | 'weba' …
  version?: string;
  BackButton: { show: () => void; hide: () => void; onClick: (cb: () => void) => void };
  ready: () => void;
  expand: () => void;
  requestFullscreen?: () => void; // Bot API 8.0+, только мобильные клиенты
  // Инсеты (Bot API 8.0+): safeArea — статусбар/вырез устройства; contentSafeArea —
  // ДОПОЛНИТЕЛЬНО высота хедера Telegram (кнопки ×/⋮) в полноэкранном режиме.
  safeAreaInset?: { top: number; bottom: number; left: number; right: number };
  contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number };
  openLink: (url: string) => void;
  openTelegramLink: (url: string) => void;
  onEvent: (event: string, cb: () => void) => void;
}

/** Мобильный ли клиент — на десктопе полный экран не нужен, там окно и так своё. */
export function isMobilePlatform(): boolean {
  const p = tg()?.platform ?? '';
  return p === 'android' || p === 'ios';
}

// Десктоп/веб-клиенты Telegram. Маркет с них открывать нельзя (только телефон).
const _DESKTOP_PLATFORMS = new Set([
  'tdesktop', 'macos', 'web', 'weba', 'webk', 'windows', 'linux', 'unigram',
]);

/** ПК/веб-клиент — или вообще не Telegram (нет контекста): маркет недоступен. */
export function isDesktopOrWeb(): boolean {
  const p = tg()?.platform;
  return !p || _DESKTOP_PLATFORMS.has(p);
}

/** Заглушка «только с телефона» — заменяет всю страницу (витрина и корзина). */
export function showMobileOnly(): void {
  document.body.innerHTML =
    "<div style=\"min-height:100vh;display:flex;align-items:center;justify-content:center;" +
    "background:#0a0a0a;color:#fafafa;text-align:center;padding:32px;" +
    "font:600 16px/1.45 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif\">" +
    `${t('mobile_only')}</div>`;
}

/** Разворачивает Mini App. На телефоне — в полный экран (8.0+), иначе expand. */
export function enterFullscreen(): void {
  const w = tg();
  if (!w) return;
  w.expand();
  if (isMobilePlatform() && typeof w.requestFullscreen === 'function') {
    try {
      w.requestFullscreen();
      // Класс включает верхний safe-area отступ: в полный экран контент иначе
      // уезжает под системную панель и кнопки Telegram (×, ⋮).
      document.documentElement.classList.add('tg-fullscreen');
      applyTopInset();
    } catch {
      /* старый клиент без fullscreen — остаёмся на expand */
    }
  }
}

/** Прячет BackButton Telegram — тогда на главном экране показывается нативная
 * «Закрыть». BackButton глобальный на весь Mini App: его включает страница
 * корзины, и без явного сокрытия на главной он остаётся висеть. */
export function hideBackButton(): void {
  try {
    tg()?.BackButton.hide();
  } catch {
    /* старый клиент без BackButton — не критично */
  }
}

/** Верхний инсет = статусбар (safeArea) + хедер Telegram (contentSafeArea).
 *
 * Пишем в CSS-переменную и переустанавливаем на событиях: инсеты приходят
 * асинхронно после requestFullscreen и меняются при повороте/сворачивании.
 * Старый клиент (нет полей) — переменная пустая, CSS падает на env()-фолбэк. */
export function applyTopInset(): void {
  const w = tg();
  if (!w) return;
  const set = (): void => {
    const top = (w.safeAreaInset?.top ?? 0) + (w.contentSafeAreaInset?.top ?? 0);
    document.documentElement.style.setProperty('--tg-top-inset', top > 0 ? `${top}px` : '');
  };
  set();
  w.onEvent('safeAreaChanged', set);
  w.onEvent('contentSafeAreaChanged', set);
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export function tg(): TgWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

const TENANT_KEY = 'tenant-slug';

/** Магазин берём из query (?tenant=gb) — его кладёт бот в кнопку витрины.
 *
 * Если параметр потерялся (переход по ссылке без него, возврат «назад» из
 * оплаты, перезапуск webview) — берём запомненный в этой вкладке. Иначе слаг
 * пустой, а от него зависит ключ корзины: покупатель добавлял тарифы в одном
 * магазине, а корзина открывалась под пустым слагом и выглядела пустой.
 *
 * sessionStorage, а не localStorage: он живёт вкладкой, поэтому открытая
 * витрина другого магазина сети не подхватит чужой слаг.
 */
export function tenantSlug(): string {
  const fromQuery = new URLSearchParams(location.search).get('tenant');
  if (fromQuery) {
    try {
      sessionStorage.setItem(TENANT_KEY, fromQuery);
    } catch {
      /* приватный режим — обойдёмся query */
    }
    return fromQuery;
  }
  try {
    return sessionStorage.getItem(TENANT_KEY) ?? '';
  } catch {
    return '';
  }
}

/** Ссылка с сохранением ?tenant между страницами index ↔ cart. */
export function withTenant(path: string): string {
  const slug = tenantSlug();
  if (!slug) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}tenant=${encodeURIComponent(slug)}`;
}

/** Состав корзины в адресе: `ids=3,7,12`.
 *
 * localStorage между index и cart.html переживает переход не везде — в вебвью
 * Telegram Desktop корзина открывалась пустой, хотя на витрине была полной.
 * Адрес доезжает всегда, поэтому он и есть источник правды, а хранилище —
 * подстраховка на случай возврата назад.
 */
export function withCart(path: string, ids: number[]): string {
  const url = withTenant(path);
  if (!ids.length) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}ids=${ids.join(',')}`;
}

/** Разбирает `ids=3,7,12` из адреса. Пусто — параметра нет или он мусорный. */
export function cartIdsFromUrl(): number[] {
  const raw = new URLSearchParams(location.search).get('ids');
  if (!raw) return [];
  return raw
    .split(',')
    .map((x) => Number(x.trim()))
    .filter((n) => Number.isInteger(n) && n > 0);
}

/** Открыть ссылку оплаты подходящим для Telegram способом. */
export function openPayUrl(url: string): void {
  const w = tg();
  const isTgLink = url.startsWith('https://t.me') || url.startsWith('tg://');
  try {
    if (isTgLink && w?.openTelegramLink) return w.openTelegramLink(url);
    if (w?.openLink) return w.openLink(url);
  } catch {
    /* провалимся на window.open */
  }
  window.open(url, '_blank');
}
